import { COMMITMENT_COPY } from "./copy";

/**
 * Commitment display rules (WEB_INTEGRATION WP-W11). Pure, so they can be
 * tested without a render.
 */

const parse = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const monthDay = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

/**
 * The timing line under a commitment's title:
 *   completed → "Completed Sep 20"
 *   otherwise from `due_at` → "Today" / "Tomorrow" / "This week" / "By Oct 3",
 *     or "Planned for Sep 12" once the date has passed — never "missed" or
 *     "overdue": there is no failed state (guide §5.4).
 *   no `due_at` → "" (no line).
 */
export const commitmentTiming = (commitment, now = new Date()) => {
  if (!commitment) return "";
  const completed = parse(commitment.completed_at);
  if (!commitment.is_open && completed) return COMMITMENT_COPY.completedOn(monthDay(completed));
  const due = parse(commitment.due_at);
  if (!due) return "";
  const days = Math.round((startOfDay(due) - startOfDay(now)) / 86400000);
  if (days < 0) return COMMITMENT_COPY.overdueSince(monthDay(due));
  if (days === 0) return COMMITMENT_COPY.dueToday;
  if (days === 1) return COMMITMENT_COPY.dueTomorrow;
  // "This week" = on or before the coming Sunday.
  const daysToSunday = (7 - now.getDay()) % 7;
  if (days <= daysToSunday) return COMMITMENT_COPY.dueThisWeek;
  return COMMITMENT_COPY.dueOn(monthDay(due));
};

/** "Today", "Yesterday", or "September 12, 2026" — the Your Progress date line. */
export const progressDay = (iso, now = new Date()) => {
  const d = parse(iso);
  if (!d) return "";
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
};

/** Newest first; the list endpoint already sorts, detail history does not. */
export const historyNewestFirst = (history) =>
  (Array.isArray(history) ? history : [])
    .filter((entry) => entry?.state && parse(entry.at))
    .slice()
    .sort((a, b) => parse(b.at) - parse(a.at));

/**
 * The header icon. `icon_key` (UD v26.5 C8) names the activity; only `walk`
 * has artwork so far (Figma 10911:20867), so every other key — and a server
 * that does not send `icon_key` yet — falls back to the open / done state
 * icon, chosen by `is_open` (never by state name). Until `icon_key` reaches
 * every environment, an open HEALTH commitment without it keeps the walk.
 */
const ICON_ARTWORK = new Set(["walk"]);

export const commitmentIconKind = (commitment) => {
  if (!commitment) return "open";
  const key = commitment.icon_key;
  if (commitment.is_open) {
    if (key && ICON_ARTWORK.has(key)) return key;
    if (!key && String(commitment.category || "").toUpperCase() === "HEALTH") return "walk";
  }
  return commitment.is_open ? "open" : "done";
};

/** The most steps drawn as circles; a bigger target (e.g. 30 a month) is a bar. */
export const MAX_STEPS = 7;

/**
 * The C1 stepper for a recurring commitment's current period, or null for a
 * one-off (`progress: null`). Occurrence `index` is 1-based; a step is done
 * when an occurrence carries its index, else by count — never guessed from
 * dates.
 */
export const stepperSteps = (progress) => {
  const target = Number(progress?.target_count);
  if (!Number.isInteger(target) || target < 1) return null;
  const completed = Math.max(0, Number(progress.completed_count) || 0);
  const byIndex = {};
  (Array.isArray(progress.occurrences) ? progress.occurrences : []).forEach((o) => {
    if (Number.isInteger(o?.index)) byIndex[o.index] = o;
  });
  return Array.from({ length: target }, (_, i) => {
    const index = i + 1;
    const occurrence = byIndex[index];
    const at = parse(occurrence?.completed_at);
    return {
      index,
      done: Boolean(occurrence) || index <= completed,
      date: at ? monthDay(at) : "",
    };
  });
};

/** "September 18, 2026 · 32 minutes" — the C5 activity line. */
export const activityDetail = (entry, now = new Date()) => {
  const day = progressDay(entry?.completed_at, now);
  const minutes = Number(entry?.duration_minutes);
  const duration =
    Number.isFinite(minutes) && minutes > 0
      ? COMMITMENT_COPY.minutes(minutes)
      : "";
  return [day, duration].filter(Boolean).join(" · ");
};
